// ============================================================================
// Componente: FinanceiroFitaCobertura.js
// Objetivo: Mostrar, na tela de Importação, quais dias/lojas já foram importados
//           na tabela da Atos e sinalizar o que falta imputar.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CalendarCheck, CheckCircle2 } from 'lucide-react';

const STATUS = {
    completo:       { label: 'Completo',       bg: '#dcfce7', text: '#15803d', cell: '#22c55e' },
    falta_dinheiro: { label: 'Falta dinheiro', bg: '#fef3c7', text: '#b45309', cell: '#f59e0b' },
    falta_cartao:   { label: 'Falta cartão',   bg: '#ffedd5', text: '#c2410c', cell: '#fb923c' },
    falta_tudo:     { label: 'Falta tudo',     bg: '#fee2e2', text: '#b91c1c', cell: '#ef4444' },
};
const ORDEM_STATUS = ['completo', 'falta_dinheiro', 'falta_cartao', 'falta_tudo'];

const ddmm = (iso) => iso.split('-').reverse().slice(0, 2).join('/');

const cardBox = { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)' };
const inputStyle = { padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', color: '#334155', backgroundColor: '#fff', outline: 'none' };

const FinanceiroFitaCobertura = ({ refreshKey }) => {
    const agora = new Date();
    const mesDefault = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;

    const [mes, setMes] = useState(mesDefault);
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');

    const token = localStorage.getItem('authToken');

    const carregar = useCallback(async () => {
        setCarregando(true); setErro('');
        try {
            const res = await fetch(`/api/fita/importacao/cobertura?mes=${mes}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Erro ${res.status}`); }
            setDados(await res.json());
        } catch (e) {
            setErro(e.message || 'Erro ao carregar a cobertura.'); setDados(null);
        } finally {
            setCarregando(false);
        }
    }, [mes, token]);

    // Carrega ao montar, ao trocar de mês e quando uma importação termina (refreshKey)
    useEffect(() => { carregar(); }, [carregar, refreshKey]);

    const temDias = dados && dados.dias && dados.dias.length > 0;

    return (
        <div style={cardBox}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                <div>
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                        <CalendarCheck size={18} color="#16a39c" /> Cobertura da Importação (Atos)
                    </h4>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                        Dias e lojas já importados e o que ainda falta imputar (cartões e/ou dinheiro).
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="month" value={mes} onChange={e => setMes(e.target.value)} style={inputStyle} />
                    <button
                        onClick={carregar}
                        disabled={carregando}
                        title="Atualizar"
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '700', fontSize: '13px', cursor: carregando ? 'not-allowed' : 'pointer' }}
                    >
                        <RefreshCw size={15} className={carregando ? 'animate-spin' : undefined} /> Atualizar
                    </button>
                </div>
            </div>

            {erro && (
                <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontWeight: '600', fontSize: '14px' }}>
                    {erro}
                </div>
            )}

            {!erro && !temDias && !carregando && (
                <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>
                    Sem dias a exibir neste mês.
                </div>
            )}

            {temDias && (
                <>
                    {/* RESUMO */}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '18px' }}>
                        {ORDEM_STATUS.map(s => (
                            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '10px', backgroundColor: STATUS[s].bg, color: STATUS[s].text, fontWeight: '700', fontSize: '13px' }}>
                                <span style={{ width: '10px', height: '10px', borderRadius: '3px', backgroundColor: STATUS[s].cell }}></span>
                                {STATUS[s].label}: {dados.resumo[s]}
                            </div>
                        ))}
                    </div>

                    {/* GRID loja × dia */}
                    <div style={{ overflowX: 'auto', border: '1px solid #f1f5f9', borderRadius: '10px' }}>
                        <table style={{ borderCollapse: 'separate', borderSpacing: '3px', padding: '6px', backgroundColor: '#fff' }}>
                            <thead style={{ backgroundColor: '#fff' }}>
                                <tr>
                                    <th style={{ position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1, padding: '4px 10px', textAlign: 'left', fontSize: '11px', fontWeight: '800', color: '#64748b' }}>LOJA</th>
                                    {dados.dias.map(d => (
                                        <th key={d} style={{ backgroundColor: '#fff', padding: '2px', fontSize: '10px', fontWeight: '700', color: '#94a3b8', minWidth: '22px' }}>{d.slice(8, 10)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {dados.lojas.map(loja => (
                                    <tr key={loja}>
                                        <td style={{ position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1, padding: '4px 10px', fontSize: '12px', fontWeight: '700', color: '#0f172a' }}>{loja}</td>
                                        {dados.dias.map(d => {
                                            const st = dados.cobertura[loja]?.[d] || 'falta_tudo';
                                            return (
                                                <td key={d} style={{ padding: 0 }}>
                                                    <div
                                                        title={`${loja} · ${ddmm(d)} — ${STATUS[st].label}`}
                                                        style={{ width: '20px', height: '20px', borderRadius: '4px', backgroundColor: STATUS[st].cell, margin: '0 auto' }}
                                                    />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* LISTA DO QUE FALTA */}
                    <div style={{ marginTop: '18px' }}>
                        {dados.faltas.length === 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderRadius: '10px', backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', fontWeight: '700', fontSize: '14px' }}>
                                <CheckCircle2 size={18} /> Tudo importado neste período. Nenhuma pendência.
                            </div>
                        ) : (
                            <>
                                <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Pendências ({dados.faltas.length})
                                </p>
                                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '10px' }}>
                                    {dados.faltas.map((f, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '8px 14px', borderBottom: i < dados.faltas.length - 1 ? '1px solid #f1f5f9' : 'none', fontSize: '13px' }}>
                                            <span style={{ color: '#334155', fontWeight: '600' }}>
                                                <strong>{ddmm(f.dia)}</strong> · {f.loja}
                                            </span>
                                            <span style={{ padding: '2px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', backgroundColor: STATUS[f.status].bg, color: STATUS[f.status].text }}>
                                                {STATUS[f.status].label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default FinanceiroFitaCobertura;
